import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Calendar } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { signInWithGoogle } from "@/utils/auth";

const Updates = () => {
  const navigate = useNavigate();

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign in failed:', error);
    }
  };

  const updates = [
    {
      date: "November 2025",
      title: "Expanded Language Support",
      description: "We've expanded our language support to 76 languages, enabling you to create native-level content for global audiences. Our AI now writes naturally in more languages than ever before.",
      type: "feature"
    },
    {
      date: "October 2025",
      title: "Advanced Image Generation",
      description: "Upgraded our image generation with the newest AI models and multiple style options. Generate realistic, cartoon, and custom-style images that perfectly match your brand and content needs.",
      type: "feature"
    },
    {
      date: "September 2025",
      title: "Google Search Console Integration",
      description: "Connect your Google Search Console to track real performance data. See actual clicks, impressions, rankings, and indexing status—all in one dashboard with real-time updates.",
      type: "feature"
    },
    {
      date: "August 2025",
      title: "Premium Backlink System",
      description: "Launched our automated backlink delivery system. Get 10 premium backlinks (DA 70+) every month, with full transparency on placements and real-time domain authority tracking.",
      type: "feature"
    },
    {
      date: "July 2025",
      title: "LLM.txt Generation",
      description: "New LLM.txt file generation helps AI search engines understand and index your content. Boost your visibility in AI-powered search results with automatically generated files.",
      type: "feature"
    },
    {
      date: "June 2025",
      title: "Enhanced Topic Clusters & Ideation",
      description: "Improved our AI analysis system to generate structured topic clusters and 30 fresh article ideas monthly. Better keyword research and content strategy planning included.",
      type: "improvement"
    }
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        title="Product Updates - Latest Features & Improvements | Seoro"
        description="Stay up to date with the latest Seoro features and improvements. Recent updates include expanded language support, advanced image generation, Google Search Console integration, and premium backlink system."
        keywords="Seoro updates, new features, product updates, SEO platform updates, content automation updates"
      />
      <Header onSignIn={handleSignIn} />
      
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="space-y-12">
          <div className="text-center space-y-4">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-foreground">
              Product Updates
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Stay up to date with the latest features and improvements.
            </p>
          </div>

          <div className="space-y-8">
            {updates.map((update, index) => (
              <div
                key={index}
                className="bg-muted/30 rounded-xl p-6 sm:p-8 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <Sparkles className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">{update.date}</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        update.type === 'feature' 
                          ? 'bg-primary/10 text-primary' 
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {update.type === 'feature' ? 'New Feature' : 'Improvement'}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-2">{update.title}</h3>
                    <p className="text-muted-foreground">{update.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-muted/30 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-foreground mb-4">Stay Updated</h2>
            <p className="text-muted-foreground mb-6">
              Follow us to get notified about new features and updates as they're released.
            </p>
            <Button
              onClick={() => navigate('/#faq')}
              className="rounded-full bg-gradient-to-r from-primary via-primary to-primary/90"
            >
              Learn More
            </Button>
          </div>
        </div>
      </main>

      <Footer onSignIn={handleSignIn} />
    </div>
  );
};

export default Updates;


